// Derived time: walk a voice's written content and assign each event its
// onset and sounding length. All arithmetic is exact rational; integer PPQ
// ticks are produced only as the final projection.

import {
  addF,
  durationQuarters,
  frac,
  fractionToNumber,
  mulF,
  PPQ,
  type Fraction,
} from './duration';
import type { NoteEvent, Voice, VoiceItem } from './types';

export interface TimedEvent {
  event: NoteEvent;
  /** Onset from the barline, in quarter notes (exact). */
  start: Fraction;
  /** Sounding length in quarter notes (exact; 0 for grace notes). */
  duration: Fraction;
  /** Onset in integer ticks at PPQ resolution (rounded at this boundary). */
  startTicks: number;
  durationTicks: number;
}

function toTicks(q: Fraction): number {
  return Math.round(fractionToNumber(mulF(q, frac(PPQ))));
}

function walk(
  items: VoiceItem[],
  factor: Fraction,
  pos: Fraction,
  out: TimedEvent[],
): Fraction {
  for (const item of items) {
    if ('kind' in item && item.kind === 'tuplet') {
      pos = walk(item.items, mulF(factor, frac(item.normal, item.actual)), pos, out);
      continue;
    }
    const event = item as NoteEvent;
    const duration = event.grace ? frac(0) : mulF(durationQuarters(event.duration), factor);
    out.push({
      event,
      start: pos,
      duration,
      startTicks: toTicks(pos),
      durationTicks: toTicks(duration),
    });
    pos = addF(pos, duration);
  }
  return pos;
}

/** Time every event in a voice (one measure's worth of content). */
export function voiceTiming(voice: Voice): TimedEvent[] {
  const out: TimedEvent[] = [];
  walk(voice.items, frac(1), frac(0), out);
  return out;
}

/** Total written length of a voice's content in quarter notes (exact). */
export function voiceLength(voice: Voice): Fraction {
  return walk(voice.items, frac(1), frac(0), []);
}
